import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { User, Crown, Mail, Flame, XCircle, Moon, Sun, Gift, Copy, Share2 } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';

// Get current season based on month
const getCurrentSeason = () => {
  const month = new Date().getMonth();
  if (month >= 2 && month <= 4) return 'spring';
  if (month >= 5 && month <= 7) return 'summer';
  if (month >= 8 && month <= 10) return 'autumn';
  return 'winter';
};

// Animated Seasonal Growing Branch Component
const SeasonalGrowingBranch = ({ className, style, delay = 0, direction = 'left', scale = 1, angle = 0 }) => {
  const isLeft = direction === 'left';
  const currentSeason = getCurrentSeason();
  
  // Calculate rotation angle - branches grow diagonally
  const rotationAngle = isLeft ? (angle || -25) : (angle || 25);
  
  const seasonColors = {
    spring: { trunk: 'hsl(30 35% 35%)', branch: 'hsl(30 35% 40%)', leaf: 'hsl(340 70% 75%)', leafAlt: 'hsl(100 40% 55%)' },
    summer: { trunk: 'hsl(30 40% 32%)', branch: 'hsl(30 40% 38%)', leaf: 'hsl(120 45% 40%)', leafAlt: 'hsl(100 50% 45%)' },
    autumn: { trunk: 'hsl(25 45% 30%)', branch: 'hsl(25 45% 35%)', leaf: 'hsl(25 80% 50%)', leafAlt: 'hsl(0 70% 55%)' },
    winter: { trunk: 'hsl(220 15% 35%)', branch: 'hsl(220 15% 40%)', leaf: 'hsl(200 30% 85%)', leafAlt: 'hsl(210 25% 90%)' }
  };
  
  const colors = seasonColors[currentSeason];
  
  return (
    <svg 
      className={`absolute pointer-events-none ${className}`}
      style={{ 
        ...style, 
        transform: `scale(${scale}) ${isLeft ? '' : 'scaleX(-1)'} rotate(${rotationAngle}deg)`,
        transformOrigin: isLeft ? 'bottom left' : 'bottom right'
      }}
      width="250" 
      height="350" 
      viewBox="0 0 250 350"
    >
      <path d="M30 350 Q25 280 40 220 Q55 160 35 110 Q15 60 45 20"
        stroke={colors.trunk} strokeWidth="5" fill="none" strokeLinecap="round"
        style={{ strokeDasharray: 500, strokeDashoffset: 500, animation: `growBranch 5s ease-out ${delay}s forwards` }}
      />
      <path d="M40 220 Q70 195 95 215 Q120 235 140 210"
        stroke={colors.branch} strokeWidth="3" fill="none" strokeLinecap="round"
        style={{ strokeDasharray: 180, strokeDashoffset: 180, animation: `growBranch 3s ease-out ${delay + 1.2}s forwards` }}
      />
      <path d="M35 110 Q70 85 100 110 Q130 135 155 110"
        stroke={colors.branch} strokeWidth="3" fill="none" strokeLinecap="round"
        style={{ strokeDasharray: 200, strokeDashoffset: 200, animation: `growBranch 3s ease-out ${delay + 1.8}s forwards` }}
      />
      <circle cx="140" cy="210" r="4" fill={colors.leaf}
        style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 3.5}s forwards` }}
      />
      <circle cx="155" cy="110" r="4" fill={colors.leaf}
        style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 3.8}s forwards` }}
      />
      <circle cx="95" cy="215" r="3" fill={colors.leafAlt}
        style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4}s forwards` }}
      />
    </svg>
  );
};

// Common timezones
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'UTC', label: 'UTC' },
];

const SettingsPage = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [user, setUser] = useState(null);
  const [streak, setStreak] = useState({ current_streak: 0, longest_streak: 0 });
  const [emailReminders, setEmailReminders] = useState(true);
  const [reminderTime, setReminderTime] = useState('08:00');
  const [timezone, setTimezone] = useState('America/New_York');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  
  // Referral state
  const [referralInfo, setReferralInfo] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [userRes, prefsRes, streakRes, referralRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(`${API}/user/preferences`, { withCredentials: true }),
        axios.get(`${API}/user/streak`, { withCredentials: true }),
        axios.get(`${API}/user/referral`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      
      setUser(userRes.data);
      setStreak(streakRes.data);
      setEmailReminders(prefsRes.data.email_reminders_enabled);
      setReminderTime(prefsRes.data.reminder_time);
      setTimezone(prefsRes.data.timezone || 'America/New_York');
      setReferralInfo(referralRes.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/user/preferences`,
        {
          email_reminders_enabled: emailReminders,
          reminder_time: reminderTime,
          timezone: timezone
        },
        { withCredentials: true }
      );
      toast.success('Preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const copyReferralLink = () => {
    if (referralInfo?.referral_link) {
      navigator.clipboard.writeText(referralInfo.referral_link);
      toast.success('Referral link copied to clipboard!');
    }
  };

  const applyReferralCode = async () => {
    if (!referralCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }
    setApplyingCode(true);
    try {
      await axios.post(
        `${API}/user/apply-referral`,
        { referral_code: referralCode.trim() },
        { withCredentials: true }
      );
      toast.success('Referral code applied successfully!');
      setReferralCode('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply referral code');
    } finally {
      setApplyingCode(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const response = await axios.post(`${API}/subscription/cancel`, {}, { withCredentials: true });
      toast.success(response.data.message);
      setShowCancelDialog(false);
      loadData(); // Refresh user data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      await axios.post(`${API}/subscription/reactivate`, {}, { withCredentials: true });
      toast.success('Subscription reactivated');
      loadData();
    } catch (error) {
      toast.error('Failed to reactivate subscription');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Seasonal Growing Branches - Background */}
      <SeasonalGrowingBranch 
        direction="left" 
        delay={0.3}
        scale={1}
        className="opacity-[0.08] fixed"
        style={{ left: '-50px', bottom: '-60px', zIndex: 0 }}
      />
      <SeasonalGrowingBranch 
        direction="right" 
        delay={0.8}
        scale={1}
        className="opacity-[0.08] fixed"
        style={{ right: '-50px', bottom: '-60px', zIndex: 0 }}
      />
      
      <div className="max-w-2xl mx-auto px-6 py-12 relative z-10">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="settings-title">
            Settings
          </h1>
          <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            Back
          </Button>
        </div>

        <div className="space-y-6">
          {/* Profile Card */}
          <Card className="p-6 border border-primary/10" data-testid="profile-card">
            <div className="flex items-start gap-4">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
              )}
              <div className="flex-1">
                <h3 className="text-xl mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {user.name}
                </h3>
                <p className="text-sm text-muted-foreground">{user.email}</p>
              </div>
            </div>
          </Card>

          {/* Subscription Card */}
          <Card className="p-6 border border-primary/10" data-testid="subscription-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Crown className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium mb-1">Subscription</h3>
                {user.is_subscribed ? (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">
                      Active {user.subscription_type || 'annual'} subscription
                      {user.subscription_cancelled && (
                        <span className="ml-2 text-yellow-600">(Cancellation pending)</span>
                      )}
                    </p>
                    {user.subscription_end && (
                      <p className="text-sm text-muted-foreground mb-4">
                        {user.subscription_cancelled ? 'Access until' : 'Renews on'} {new Date(user.subscription_end).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    )}
                    {user.subscription_cancelled ? (
                      <Button 
                        size="sm" 
                        onClick={handleReactivateSubscription}
                        data-testid="reactivate-btn"
                      >
                        Reactivate Subscription
                      </Button>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => setShowCancelDialog(true)}
                        data-testid="cancel-subscription-btn"
                      >
                        <XCircle className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Cancel Subscription
                      </Button>
                    )}
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">No active subscription</p>
                    <Button size="sm" onClick={() => navigate('/subscribe')} data-testid="subscribe-btn">
                      Subscribe Now
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Reading Streak Card */}
          <Card className="p-6 border border-primary/10" data-testid="streak-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <Flame className="w-6 h-6 text-orange-600" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium mb-1">Reading Streak</h3>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <p className="text-2xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {streak.current_streak}
                    </p>
                    <p className="text-sm text-muted-foreground">Current Streak</p>
                  </div>
                  <div>
                    <p className="text-2xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {streak.longest_streak}
                    </p>
                    <p className="text-sm text-muted-foreground">Longest Streak</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Appearance Card - Dark Mode */}
          <Card className="p-6 border border-primary/10" data-testid="appearance-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                {theme === 'dark' ? (
                  <Moon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                ) : (
                  <Sun className="w-6 h-6 text-primary" strokeWidth={1.5} />
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium mb-1">Appearance</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Choose your preferred theme
                </p>
                <div className="flex items-center justify-between">
                  <Label htmlFor="dark-mode" className="flex items-center gap-2 cursor-pointer">
                    <span>Dark Mode</span>
                  </Label>
                  <Switch
                    id="dark-mode"
                    checked={theme === 'dark'}
                    onCheckedChange={toggleTheme}
                    data-testid="dark-mode-toggle"
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Email Preferences Card */}
          <Card className="p-6 border border-primary/10" data-testid="email-preferences-card">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium mb-1">Email Reminders</h3>
                <p className="text-sm text-muted-foreground">
                  Receive daily devotional reminders
                </p>
              </div>
            </div>

            <div className="space-y-4 ml-16">
              <div className="flex items-center justify-between">
                <Label htmlFor="email-reminders" className="text-sm">
                  Enable daily reminders
                </Label>
                <Switch
                  id="email-reminders"
                  checked={emailReminders}
                  onCheckedChange={setEmailReminders}
                  data-testid="email-reminders-toggle"
                />
              </div>

              {emailReminders && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="timezone" className="text-sm mb-2 block">
                      Your timezone
                    </Label>
                    <Select value={timezone} onValueChange={setTimezone}>
                      <SelectTrigger className="w-64" data-testid="timezone-select">
                        <SelectValue placeholder="Select timezone" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMEZONES.map((tz) => (
                          <SelectItem key={tz.value} value={tz.value}>
                            {tz.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="reminder-time" className="text-sm mb-2 block">
                      Reminder time (in your local time)
                    </Label>
                    <Input
                      id="reminder-time"
                      type="time"
                      value={reminderTime}
                      onChange={(e) => setReminderTime(e.target.value)}
                      className="w-40"
                      data-testid="reminder-time-input"
                    />
                  </div>
                </div>
              )}

              <Button
                onClick={savePreferences}
                disabled={saving}
                size="sm"
                data-testid="save-preferences-btn"
              >
                {saving ? 'Saving...' : 'Save Preferences'}
              </Button>
            </div>
          </Card>

          {/* Referral Program Card */}
          <Card className="p-6 border border-primary/10 bg-gradient-to-br from-primary/5 to-transparent" data-testid="referral-card">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Gift className="w-6 h-6 text-green-600" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium mb-1">Refer a Friend</h3>
                <p className="text-sm text-muted-foreground">
                  Get 1 free month for each friend you refer (up to 6 months/year)
                </p>
              </div>
            </div>

            {referralInfo && (
              <div className="space-y-4 ml-16">
                {/* Referral Stats */}
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 rounded-lg bg-background">
                    <p className="text-2xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {referralInfo.qualified_referrals}
                    </p>
                    <p className="text-xs text-muted-foreground">Successful Referrals</p>
                  </div>
                  <div className="p-3 rounded-lg bg-background">
                    <p className="text-2xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {referralInfo.pending_referrals}
                    </p>
                    <p className="text-xs text-muted-foreground">Pending</p>
                  </div>
                  <div className="p-3 rounded-lg bg-background">
                    <p className="text-2xl font-light text-green-600" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {referralInfo.free_months_available}
                    </p>
                    <p className="text-xs text-muted-foreground">Free Months Available</p>
                  </div>
                </div>

                {/* Share Link */}
                <div>
                  <Label className="text-sm mb-2 block">Your referral link</Label>
                  <div className="flex gap-2">
                    <Input
                      value={referralInfo.referral_link}
                      readOnly
                      className="text-sm"
                      data-testid="referral-link-input"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyReferralLink}
                      data-testid="copy-referral-btn"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Share this link with friends. You'll get a free month when they subscribe!
                  </p>
                </div>

                {/* How it works */}
                <div className="text-sm text-muted-foreground bg-background rounded-lg p-4">
                  <p className="font-medium mb-2">How it works:</p>
                  <ul className="space-y-1 text-xs">
                    <li>• Your friend signs up using your link and subscribes</li>
                    <li>• Annual plan: You get a free month immediately</li>
                    <li>• Monthly plan: You get a free month after they pay for 2 months</li>
                    <li>• Free months are added after your current subscription ends</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Apply a referral code (if user hasn't been referred) */}
            {referralInfo && !user?.referred_by && (
              <div className="mt-4 pt-4 border-t ml-16">
                <Label className="text-sm mb-2 block">Have a referral code?</Label>
                <div className="flex gap-2">
                  <Input
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    placeholder="Enter code (e.g., ROOTED123ABC)"
                    className="text-sm uppercase"
                    data-testid="apply-referral-code-input"
                  />
                  <Button
                    size="sm"
                    onClick={applyReferralCode}
                    disabled={applyingCode}
                    data-testid="apply-referral-btn"
                  >
                    {applyingCode ? 'Applying...' : 'Apply'}
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* Admin Access */}
          <Card className="p-6 border border-primary/10" data-testid="admin-card">
            <h3 className="text-lg font-medium mb-3">Admin Access</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Manage devotional content for all 365 days
            </p>
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin')} 
              data-testid="admin-access-btn"
            >
              Admin Dashboard
            </Button>
          </Card>

          {/* Logout */}
          <Card className="p-6 border border-primary/10" data-testid="logout-card">
            <h3 className="text-lg font-medium mb-3">Account</h3>
            <Button 
              variant="destructive" 
              onClick={handleLogout} 
              data-testid="logout-btn"
            >
              Sign Out
            </Button>
          </Card>
        </div>
      </div>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription?</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your subscription? You will continue to have access until your current billing period ends
              {user?.subscription_end && (
                <span className="font-medium">
                  {' '}on {new Date(user.subscription_end).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              )}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowCancelDialog(false)}
              disabled={cancelling}
            >
              Keep Subscription
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancelSubscription}
              disabled={cancelling}
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;